import { useState } from "react"
import {
  origemDoIdioma,
  persistirIdiomaDoUsuario,
  resolverIdioma,
  aplicarIdiomaResolvido,
  seguindoOSistema,
  seguirPadraoDoSistema,
} from "../i18n/languageCascade"
import { Box, Divider, IconButton, Menu, MenuItem, Tooltip } from "@mui/material"
import SettingsSuggestIcon from "@mui/icons-material/SettingsSuggest"
import LanguageIcon from "@mui/icons-material/Language"
import "flag-icons/css/flag-icons.min.css"
import { useTranslation } from "react-i18next"
import { supportedLanguages } from "../i18n"

/** Bandeira SVG discreta (flag-icons). */
function Flag({ country, size = 16 }: { country: string; size?: number }) {
  return (
    <Box
      component="span"
      className={`fi fi-${country}`}
      sx={{
        width: size,
        height: Math.round(size * 0.75),
        borderRadius: "2px",
        backgroundSize: "cover",
        boxShadow: "0 0 0 1px rgba(0,0,0,0.08)",
        flexShrink: 0,
      }}
    />
  )
}

/**
 * Seletor de idioma (dropdown discreto com bandeira + código) na barra superior.
 * A escolha é persistida pelo i18next (localStorage, chave i18nextLng).
 * Para adicionar um idioma novo, registre-o em src/i18n (code/short/country).
 */
export default function LanguageSelector() {
  const { i18n, t } = useTranslation()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  const current = i18n.resolvedLanguage || i18n.language

  // De qual nível da cascata veio o idioma atual. Sem isto, "por que entrou
  // em português?" só se responde lendo código — e a resposta muda conforme o
  // backend passa a devolver cada campo.
  const origem = origemDoIdioma()

  /**
   * Volta ao padrão do sistema.
   *
   * Logado, isto significa "sem idioma PRÓPRIO": o usuário volta a herdar do
   * haras e do parceiro, e só cai no aparelho se nenhum deles definir nada.
   * Por isso a cascata é consultada de novo logo em seguida.
   *
   * Deslogado, é só esquecer a preferência e seguir o aparelho.
   */
  const handleSystem = async () => {
    setAnchorEl(null)
    await seguirPadraoDoSistema()

    let logado = false
    try {
      const u = JSON.parse(localStorage.getItem('user') || 'null')
      logado = Boolean(u?.userId)
      if (logado) {
        persistirIdiomaDoUsuario(null)
        const r = await resolverIdioma(u.userId)
        await aplicarIdiomaResolvido(r)
      }
    } catch {
      /* sem registro local — fica no idioma do aparelho */
    }
  }

  const handleSelect = (code: string) => {
    i18n.changeLanguage(code)
    // A escolha aqui é a do NÍVEL USUÁRIO — o mais específico da cascata.
    // Gravada no servidor para valer no próximo acesso e nos outros
    // dispositivos, como já acontece com o tema.
    persistirIdiomaDoUsuario(code)
    setAnchorEl(null)
  }

  return (
    <>
      <Tooltip
        title={`${t("common.language")} — ${t(`language.source.${origem}`)}`}
        arrow
      >
        <IconButton
          color="inherit"
          size="small"
          onClick={(e) => setAnchorEl(e.currentTarget)}
          aria-label={t("common.language")}
        >
          <LanguageIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        MenuListProps={{ dense: true }}
      >
        {/* Sem esta opção não há caminho de volta: escolhido um idioma, o
            aparelho deixa de ser consultado para sempre. */}
        <MenuItem
          selected={seguindoOSistema()}
          onClick={() => void handleSystem()}
          sx={{ gap: 1.25, fontSize: 13 }}
        >
          <SettingsSuggestIcon sx={{ fontSize: 18, color: "text.secondary" }} />
          <Box component="span" sx={{ color: "text.secondary" }}>
            {t("common.systemDefault")}
          </Box>
        </MenuItem>
        <Divider />

        {supportedLanguages.map((lng) => (
          <MenuItem
            key={lng.code}
            selected={!seguindoOSistema() && current === lng.code}
            onClick={() => handleSelect(lng.code)}
            sx={{ gap: 1.25, fontSize: 13 }}
          >
            <Flag country={lng.country} />
            <Box component="span" sx={{ letterSpacing: "0.04em", color: "text.secondary" }}>
              {lng.short}
            </Box>
          </MenuItem>
        ))}
      </Menu>
    </>
  )
}
